import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import {
  getMultiFactorResolver,
  RecaptchaVerifier,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  FactorId,
} from 'firebase/auth';
import { signIn, signOut } from '../services/authService';
import { db, auth } from '../firebase';
import { COLORS } from '../theme';
import { getFriendlyAuthErrorMessage } from '../utils/authErrorMessages';

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // PROMPT #327: real login break for ckclarence@gmail.com — signInWithEmailAndPassword()
  // throws auth/multi-factor-auth-required for any account with an enrolled second factor
  // (see TECH_SPEC.md §15). This is the minimal MFA *challenge-completion* UI, not the
  // enrollment UI Tammy scoped separately — see this prompt's report for that distinction.
  const [mfaResolver, setMfaResolver] = useState(null);
  const [mfaVerificationId, setMfaVerificationId] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [mfaBusy, setMfaBusy] = useState(false);
  const recaptchaVerifierRef = useRef(null);

  const inputStyle = {
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: COLORS.sidebar,
    border: `1px solid ${COLORS.border}`,
    color: COLORS.textPrimary,
    padding: '10px 14px',
    borderRadius: '6px',
    fontSize: '14px',
    outline: 'none',
    marginBottom: '12px',
  };

  // Shared by both the direct sign-in success path and the post-MFA
  // resolveSignIn() success path — same HC-SECURITY-005 role check either way.
  async function completeLoginWithUser(user) {
    const tokenResult = await user.getIdTokenResult(true);
    let role = tokenResult.claims.role;

    // HC-SECURITY-005: claims may be missing/stale — fall back to the
    // Firestore doc directly before rejecting the login. Firestore is the
    // source of truth; see /home/snoopy/investigations/hc-security-005-investigation.md.
    if (role !== 'platform') {
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        role = snap.data()?.role;
      } catch (_) {
        // Firestore fallback failed too — role stays whatever claims gave us.
      }
    }

    if (role !== 'platform') {
      await signOut();
      resetMfaState();
      setError('Not authorized for this portal.');
      setLoading(false);
      return;
    }
    navigate('/dashboard');
  }

  function resetMfaState() {
    if (recaptchaVerifierRef.current) {
      recaptchaVerifierRef.current.clear();
      recaptchaVerifierRef.current = null;
    }
    setMfaResolver(null);
    setMfaVerificationId(null);
    setMfaCode('');
    setMfaError('');
    setMfaBusy(false);
    setLoading(false);
  }

  // Renders a visible RecaptchaVerifier and sends the SMS code for the
  // resolver's enrolled phone factor — the same RecaptchaVerifier/
  // PhoneAuthProvider chain confirmed working end-to-end against a real
  // domain in tonight's Prompt #325/#326 manual enrollment test (see
  // TECH_SPEC.md §15), now as real committed app code instead of a
  // throwaway test page. Only PHONE_SMS is an enabled MFA provider on this
  // project (config/enabledProviders), so a single phone hint is expected.
  const startPhoneVerification = useCallback(async (resolver) => {
    setMfaError('');
    setMfaBusy(true);
    setMfaVerificationId(null);
    try {
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear();
        recaptchaVerifierRef.current = null;
      }
      const verifier = new RecaptchaVerifier(auth, 'mfa-recaptcha-container', { size: 'normal' });
      recaptchaVerifierRef.current = verifier;
      const hint = resolver.hints.find(h => h.factorId === FactorId.PHONE) || resolver.hints[0];
      const phoneAuthProvider = new PhoneAuthProvider(auth);
      const verificationId = await phoneAuthProvider.verifyPhoneNumber(
        { multiFactorHint: hint, session: resolver.session },
        verifier
      );
      setMfaVerificationId(verificationId);
    } catch (err) {
      setMfaError(getFriendlyAuthErrorMessage(err));
    } finally {
      setMfaBusy(false);
    }
  }, []);

  // Fires once per MFA challenge, right after the credentials step hands us
  // a resolver — waits for the DOM commit (the recaptcha container only
  // exists once the mfa-code branch below renders) before constructing the
  // RecaptchaVerifier against it.
  useEffect(() => {
    if (mfaResolver) {
      startPhoneVerification(mfaResolver);
    }
  }, [mfaResolver, startPhoneVerification]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { user } = await signIn(email, password);
      await completeLoginWithUser(user);
    } catch (err) {
      if (err && err.code === 'auth/multi-factor-auth-required') {
        setMfaResolver(getMultiFactorResolver(auth, err));
        setLoading(false);
        return;
      }
      // PROMPT #308: never show err.message/err.code (a raw Firebase Auth
      // SDK string, e.g. "Firebase: Error (auth/invalid-credential)")
      // directly to the user — map it to a friendly, branded message.
      setError(getFriendlyAuthErrorMessage(err));
      setLoading(false);
    }
  }

  async function handleVerifyCode(e) {
    e.preventDefault();
    if (!mfaResolver || !mfaVerificationId) return;
    setMfaError('');
    setMfaBusy(true);
    try {
      const credential = PhoneAuthProvider.credential(mfaVerificationId, mfaCode.trim());
      const assertion = PhoneMultiFactorGenerator.assertion(credential);
      const { user } = await mfaResolver.resolveSignIn(assertion);
      await completeLoginWithUser(user);
    } catch (err) {
      setMfaError(getFriendlyAuthErrorMessage(err));
      setMfaBusy(false);
    }
  }

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: COLORS.bg,
    }}>
      <div style={{
        backgroundColor: COLORS.surface,
        border: `1px solid ${COLORS.border}`,
        borderRadius: '8px',
        padding: '40px',
        width: '380px',
        boxSizing: 'border-box',
      }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <img src="/logo192.png" alt="WorkrMatch" width={120} height={120} style={{ borderRadius: "16px", display: "block", margin: "0 auto 0.25rem" }} />
          <div style={{
            fontSize: '13px',
            color: COLORS.textSecondary,
          }}>
            Super Admin Portal
          </div>
        </div>

        {!mfaResolver ? (
          <form onSubmit={handleSubmit}>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              style={inputStyle}
              onFocus={e => e.target.style.borderColor = COLORS.accent}
              onBlur={e => e.target.style.borderColor = COLORS.border}
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              style={inputStyle}
              onFocus={e => e.target.style.borderColor = COLORS.accent}
              onBlur={e => e.target.style.borderColor = COLORS.border}
            />

            {error && (
              <div style={{
                color: COLORS.danger,
                fontSize: '13px',
                marginBottom: '12px',
                padding: '8px 12px',
                backgroundColor: COLORS.danger + '18',
                borderRadius: '4px',
                border: `1px solid ${COLORS.danger}40`,
              }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                height: '44px',
                backgroundColor: loading ? COLORS.border : COLORS.accent,
                color: '#1B1B1F',
                fontWeight: '600',
                fontSize: '14px',
                border: 'none',
                borderRadius: '6px',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.15s',
              }}
              onMouseEnter={e => { if (!loading) e.target.style.backgroundColor = COLORS.accentHover; }}
              onMouseLeave={e => { if (!loading) e.target.style.backgroundColor = COLORS.accent; }}
            >
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyCode}>
            <div style={{ fontSize: '13px', color: COLORS.textSecondary, marginBottom: '16px', textAlign: 'center' }}>
              {mfaVerificationId
                ? 'Enter the verification code sent to your phone.'
                : 'Two-factor verification required. Solve the check below to receive your code.'}
            </div>

            <div
              id="mfa-recaptcha-container"
              style={{
                display: mfaVerificationId ? 'none' : 'flex',
                justifyContent: 'center',
                marginBottom: '16px',
                minHeight: mfaVerificationId ? '0' : '78px',
              }}
            />

            {mfaVerificationId && (
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="Verification code"
                value={mfaCode}
                onChange={e => setMfaCode(e.target.value)}
                required
                autoFocus
                style={inputStyle}
                onFocus={e => e.target.style.borderColor = COLORS.accent}
                onBlur={e => e.target.style.borderColor = COLORS.border}
              />
            )}

            {mfaError && (
              <div style={{
                color: COLORS.danger,
                fontSize: '13px',
                marginBottom: '12px',
                padding: '8px 12px',
                backgroundColor: COLORS.danger + '18',
                borderRadius: '4px',
                border: `1px solid ${COLORS.danger}40`,
              }}>
                {mfaError}
              </div>
            )}

            {mfaVerificationId && (
              <button
                type="submit"
                disabled={mfaBusy || !mfaCode}
                style={{
                  width: '100%',
                  height: '44px',
                  backgroundColor: (mfaBusy || !mfaCode) ? COLORS.border : COLORS.accent,
                  color: '#1B1B1F',
                  fontWeight: '600',
                  fontSize: '14px',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: (mfaBusy || !mfaCode) ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.15s',
                }}
              >
                {mfaBusy ? 'Verifying…' : 'Verify'}
              </button>
            )}

            <div style={{ marginTop: '16px', textAlign: 'center', display: 'flex', justifyContent: 'center', gap: '16px' }}>
              {mfaVerificationId && (
                <button
                  type="button"
                  onClick={() => startPhoneVerification(mfaResolver)}
                  disabled={mfaBusy}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: COLORS.accent,
                    fontSize: '13px',
                    cursor: mfaBusy ? 'not-allowed' : 'pointer',
                    padding: 0,
                  }}
                >
                  Resend code
                </button>
              )}
              <button
                type="button"
                onClick={resetMfaState}
                style={{
                  background: 'none',
                  border: 'none',
                  color: COLORS.textSecondary,
                  fontSize: '13px',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                ← Back to sign in
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
